import React, { useState, useEffect, useRef } from "react";
import "./App.css";
import ExpenseForm from "./components/ExpenseForm";
import ExpenseList from "./components/ExpenseList";
import ReportHeader from "./components/ReportHeader";
import TimeRangeForm from "./components/TimeRangeForm";
import HistoryPanel from "./components/HistoryPanel";
import {
  TravelExpense,
  TravelExpenseReport,
  ExpenseFormData,
  ExpenseCategory,
} from "./types/expense";
import { generateExpenseReportPDF } from "./utils/pdfGenerator";
import { HistoryManager } from "./utils/historyManager";
import { fileStorage } from "./utils/fileStorage";

function App() {
  // Load data from localStorage on initial load
  const [expenses, setExpenses] = useState<TravelExpense[]>([]);
  const [isLoadingExpenses, setIsLoadingExpenses] = useState(true);

  const [reportInfo, setReportInfo] = useState<Partial<TravelExpenseReport>>(
    () => {
      const savedReportInfo = localStorage.getItem("travel-report-info");
      return savedReportInfo
        ? JSON.parse(savedReportInfo)
        : {
            employeeName: "",
            employeeStreet: "",
            employeeCity: "",
            employeeZipCode: "",
            quarterPeriod: "",
          };
    },
  );

  const [activeTab, setActiveTab] = useState<ExpenseCategory>(
    ExpenseCategory.BELEGE,
  );

  const [formClearTrigger, setFormClearTrigger] = useState<number>(0);
  const [showHistory, setShowHistory] = useState<boolean>(false);
  const historyManager = useRef(HistoryManager.getInstance());

  // Initialize file storage and load expenses on app start
  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Initialize IndexedDB
        await fileStorage.init();

        // Load expenses from localStorage
        const savedExpenses = localStorage.getItem("travel-expenses");
        if (savedExpenses) {
          const parsed = JSON.parse(savedExpenses);

          // Restore File objects from IndexedDB
          const expensesWithFiles = await Promise.all(
            parsed.map(async (expense: any) => {
              let restoredFile: File | undefined;

              if (expense.receiptFileId) {
                try {
                  const file = await fileStorage.getFile(expense.receiptFileId);
                  restoredFile = file || undefined;
                } catch (error) {
                  console.error(`Failed to restore file ${expense.receiptFileId}:`, error);
                }
              }

              return {
                ...expense,
                receipt: restoredFile,
                // Migrate old data format to include metadata if missing
                receiptMetadata: expense.receiptMetadata ||
                  (restoredFile ? {
                    name: restoredFile.name,
                    size: restoredFile.size,
                    type: restoredFile.type,
                    lastModified: restoredFile.lastModified,
                  } : undefined),
              };
            })
          );

          setExpenses(expensesWithFiles);
        }
      } catch (error) {
        console.error("Failed to initialize app:", error);
      } finally {
        setIsLoadingExpenses(false);
      }
    };

    initializeApp();
  }, []);

  // Save expenses to localStorage whenever they change (but exclude File objects)
  useEffect(() => {
    if (!isLoadingExpenses) {
      const expensesToSave = expenses.map(expense => ({
        ...expense,
        receipt: undefined, // Don't save File objects to localStorage
      }));
      localStorage.setItem("travel-expenses", JSON.stringify(expensesToSave));
    }
  }, [expenses, isLoadingExpenses]);

  // Cleanup orphaned files periodically
  useEffect(() => {
    const cleanupOrphanedFiles = async () => {
      if (!isLoadingExpenses && expenses.length > 0) {
        const referencedFileIds = expenses
          .map(expense => expense.receiptFileId)
          .filter(Boolean) as string[];

        try {
          await fileStorage.cleanupOrphanedFiles(referencedFileIds);
        } catch (error) {
          console.error("Failed to cleanup orphaned files:", error);
        }
      }
    };

    // Run cleanup after expenses are loaded and then every 5 minutes
    if (!isLoadingExpenses) {
      cleanupOrphanedFiles();
      const interval = setInterval(cleanupOrphanedFiles, 5 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [expenses, isLoadingExpenses]);

  // Save report info to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("travel-report-info", JSON.stringify(reportInfo));
  }, [reportInfo]);

  const addExpense = async (expenseData: ExpenseFormData) => {
    let receiptFileId: string | undefined;

    // Store receipt file in IndexedDB if present
    if (expenseData.receipt) {
      try {
        receiptFileId = await fileStorage.storeFile(expenseData.receipt);
      } catch (error) {
        console.error("Failed to store receipt file:", error);
        alert("Fehler beim Speichern der Beleg-Datei. Bitte versuchen Sie es erneut.");
        return;
      }
    }

    const newExpense: TravelExpense = {
      id: Date.now().toString(),
      date: expenseData.date,
      description: expenseData.description,
      category: expenseData.category,
      amount: parseFloat(expenseData.amount),
      currency: "EUR",
      location: expenseData.location,
      duration: expenseData.duration || "",
      receipt: expenseData.receipt,
      receiptMetadata: expenseData.receipt
        ? {
            name: expenseData.receipt.name,
            size: expenseData.receipt.size,
            type: expenseData.receipt.type,
            lastModified: expenseData.receipt.lastModified,
          }
        : undefined,
      receiptFileId: receiptFileId,
    };

    setExpenses((prev) => [...prev, newExpense]);
  };

  const deleteExpense = async (id: string) => {
    const expenseToDelete = expenses.find(exp => exp.id === id);

    // Clean up stored receipt file if it exists
    if (expenseToDelete?.receiptFileId) {
      try {
        await fileStorage.deleteFile(expenseToDelete.receiptFileId);
      } catch (error) {
        console.error("Failed to delete receipt file:", error);
      }
    }

    setExpenses((prev) => prev.filter((expense) => expense.id !== id));
  };

  const updateExpense = async (id: string, updatedData: ExpenseFormData) => {
    const existingExpense = expenses.find(exp => exp.id === id);
    if (!existingExpense) return;

    let receiptFileId = existingExpense.receiptFileId;

    // Handle receipt file changes
    if (updatedData.receipt && updatedData.receipt !== existingExpense.receipt) {
      try {
        // Store new receipt file
        receiptFileId = await fileStorage.storeFile(updatedData.receipt);

        // Clean up old file if it exists
        if (existingExpense.receiptFileId) {
          await fileStorage.deleteFile(existingExpense.receiptFileId);
        }
      } catch (error) {
        console.error("Failed to update receipt file:", error);
        alert("Fehler beim Aktualisieren der Beleg-Datei. Bitte versuchen Sie es erneut.");
        return;
      }
    } else if (!updatedData.receipt && existingExpense.receiptFileId) {
      // Receipt was removed, clean up stored file
      try {
        await fileStorage.deleteFile(existingExpense.receiptFileId);
        receiptFileId = undefined;
      } catch (error) {
        console.error("Failed to delete receipt file:", error);
      }
    }

    setExpenses((prev) =>
      prev.map((expense) =>
        expense.id === id
          ? {
              ...expense,
              date: updatedData.date,
              description: updatedData.description,
              category: updatedData.category,
              amount: parseFloat(updatedData.amount),
              location: updatedData.location,
              duration: updatedData.duration || "",
              receipt: updatedData.receipt || expense.receipt,
              receiptMetadata:
                updatedData.receiptMetadata || expense.receiptMetadata,
              receiptFileId: receiptFileId,
            }
          : expense,
      ),
    );
  };

  const handleReportInfoChange = (
    newReportInfo: Partial<TravelExpenseReport>,
  ) => {
    setReportInfo(newReportInfo);
  };

  const generatePDF = async () => {
    if (expenses.length === 0) {
      alert(
        "Bitte fügen Sie mindestens eine Ausgabe hinzu, bevor Sie einen PDF-Bericht erstellen.",
      );
      return;
    }

    const totalAmount = expenses.reduce(
      (sum, expense) => sum + expense.amount,
      0,
    );

    const reportData: TravelExpenseReport = {
      employeeName: reportInfo.employeeName || "Unbekannter Mitarbeiter",
      employeeStreet: reportInfo.employeeStreet || "",
      employeeCity: reportInfo.employeeCity || "",
      employeeZipCode: reportInfo.employeeZipCode || "",
      quarterPeriod: reportInfo.quarterPeriod || "",
      expenses: expenses,
      totalAmount: totalAmount,
    };

    try {
      await generateExpenseReportPDF(reportData);
      // Save to history after successful PDF generation
      const timestamp = new Date().toLocaleString('de-DE');
      historyManager.current.addEntry(expenses, reportInfo, `PDF erstellt am ${timestamp}`);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert(
        "Fehler beim Erstellen des PDF-Berichts. Bitte versuchen Sie es erneut.",
      );
    }
  };

  const clearAllExpenses = async () => {
    if (expenses.length === 0) return;

    if (
      window.confirm(
        "Sind Sie sicher, dass Sie alle Ausgaben löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.",
      )
    ) {
      // Clean up all stored receipt files
      try {
        await fileStorage.clearAllFiles();
      } catch (error) {
        console.error("Failed to clear stored files:", error);
      }

      setExpenses([]);
      localStorage.removeItem("travel-expenses");
    }
  };

  const clearAllData = async () => {
    if (
      window.confirm(
        "Sind Sie sicher, dass Sie alle Daten einschließlich Ausgaben, Berichtsinformationen und Verlauf löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.",
      )
    ) {
      // Clean up all stored receipt files
      try {
        await fileStorage.clearAllFiles();
      } catch (error) {
        console.error("Failed to clear stored files:", error);
      }

      // Clear history
      try {
        historyManager.current.clearHistory();
      } catch (error) {
        console.error("Failed to clear history:", error);
      }

      setExpenses([]);
      setReportInfo({
        employeeName: "",
        employeeStreet: "",
        employeeCity: "",
        employeeZipCode: "",
        quarterPeriod: "",
      });
      localStorage.removeItem("travel-expenses");
      localStorage.removeItem("travel-report-info");
      localStorage.removeItem("travel-expense-form");
      // Trigger form clearing
      setFormClearTrigger(Date.now());
    }
  };

  const handleRestoreHistory = async (restoredExpenses: TravelExpense[], restoredReportInfo: Partial<TravelExpenseReport>) => {
    try {
      let missingFilesCount = 0;

      // Restore File objects from IndexedDB for the restored expenses
      const expensesWithFiles = await Promise.all(
        restoredExpenses.map(async (expense) => {
          let restoredFile: File | undefined;

          if (expense.receiptFileId) {
            try {
              const file = await fileStorage.getFile(expense.receiptFileId);
              if (file) {
                restoredFile = file;
              } else {
                missingFilesCount++;
                console.warn(`File not found for expense "${expense.description}": ${expense.receiptFileId}`);
              }
            } catch (error) {
              missingFilesCount++;
              console.error(`Failed to restore file ${expense.receiptFileId} from history:`, error);
            }
          }

          return {
            ...expense,
            receipt: restoredFile,
          };
        })
      );

      setExpenses(expensesWithFiles);
      setReportInfo(restoredReportInfo as Partial<TravelExpenseReport>);

      // Warn user if some files couldn't be restored
      if (missingFilesCount > 0) {
        alert(`Verlauf wiederhergestellt. Hinweis: ${missingFilesCount} Beleg-Datei(en) konnten nicht gefunden werden und sind möglicherweise nicht mehr verfügbar.`);
      }
    } catch (error) {
      console.error("Failed to restore history:", error);
      alert("Fehler beim Wiederherstellen des Verlaufs. Einige Beleg-Dateien könnten nicht verfügbar sein.");
      // Still restore the data without files as fallback
      setExpenses(restoredExpenses);
      setReportInfo(restoredReportInfo as Partial<TravelExpenseReport>);
    }
  };

  return (
    <div className="App">
      <div className="container">
        <ReportHeader
          reportInfo={reportInfo}
          onReportInfoChange={handleReportInfoChange}
          onClearAllData={clearAllData}
          onClearAllExpenses={clearAllExpenses}
          hasExpenses={expenses.length > 0}
          onShowHistory={() => setShowHistory(true)}
        />

        {/* History Panel */}
        <HistoryPanel
          isOpen={showHistory}
          onClose={() => setShowHistory(false)}
          onRestoreHistory={handleRestoreHistory}
        />

        <div className="main-content">
          {/* Loading indicator */}
          {isLoadingExpenses && (
            <div className="loading-indicator" style={{ textAlign: 'center', padding: '20px' }}>
              <i className="fas fa-spinner fa-spin"></i> Lade Ausgaben...
            </div>
          )}

          {/* Tab Navigation */}
          {!isLoadingExpenses && (
            <div className="tab-navigation">
              <div className="tab-buttons">
                <button
                  className={`tab-button ${activeTab === ExpenseCategory.BELEGE ? "active" : ""}`}
                  onClick={() => setActiveTab(ExpenseCategory.BELEGE)}
                >
                  <i className="fas fa-receipt"></i>
                  Belege
                </button>
                <button
                  className={`tab-button ${activeTab === ExpenseCategory.SPESENABRECHNUNG ? "active" : ""}`}
                  onClick={() => setActiveTab(ExpenseCategory.SPESENABRECHNUNG)}
                >
                  <i className="fas fa-file-invoice"></i>
                  Spesenabrechnung
                </button>
              </div>
              <div className="tab-timerange">
                <TimeRangeForm
                  reportInfo={reportInfo}
                  onReportInfoChange={handleReportInfoChange}
                />
              </div>
            </div>
          )}

          {/* Tab Content */}
          {!isLoadingExpenses && (
            <div className="tab-content">
            {activeTab === ExpenseCategory.BELEGE && (
              <div className="tab-panel">
                <div className="category-layout">
                  <div className="form-section">
                    <ExpenseForm
                      onAddExpense={addExpense}
                      fixedCategory={ExpenseCategory.BELEGE}
                      clearTrigger={formClearTrigger}
                    />
                  </div>
                  <div className="table-section">
                    <ExpenseList
                      expenses={expenses.filter(
                        (e) => e.category === ExpenseCategory.BELEGE,
                      )}
                      onDeleteExpense={deleteExpense}
                      onUpdateExpense={updateExpense}
                      showCategoryFilter={false}
                      summaryOnly={false}
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === ExpenseCategory.SPESENABRECHNUNG && (
              <div className="tab-panel">
                <div className="category-layout">
                  <div className="form-section">
                    <ExpenseForm
                      onAddExpense={addExpense}
                      fixedCategory={ExpenseCategory.SPESENABRECHNUNG}
                      clearTrigger={formClearTrigger}
                    />
                  </div>
                  <div className="table-section">
                    <ExpenseList
                      expenses={expenses.filter(
                        (e) => e.category === ExpenseCategory.SPESENABRECHNUNG,
                      )}
                      onDeleteExpense={deleteExpense}
                      onUpdateExpense={updateExpense}
                      showCategoryFilter={false}
                      summaryOnly={false}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Summary Table */}
            {expenses.length > 0 && (
              <div className="summary-section">
                <ExpenseList
                  expenses={expenses}
                  onDeleteExpense={deleteExpense}
                  onUpdateExpense={updateExpense}
                  showCategoryFilter={true}
                  summaryOnly={true}
                />
              </div>
            )}

            {expenses.length > 0 && (
              <div className="actions-section">
                <button onClick={generatePDF} className="generate-pdf-btn">
                  <i className="fas fa-file-pdf"></i> PDF-Bericht erstellen
                </button>
              </div>
            )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default App;
